import { Fragment } from "react";
import { Dialog, Transition, Disclosure } from "@headlessui/react";
import {
  XMarkIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { useLocation } from "react-router-dom";
import useGeneralStore from "store/general";
import useGlobalStore from "store/global";
import useAuth from "hooks/useAuth";
import { navigation } from "utils/navigation";
import { isModuleEnabledForCompany } from "utils/modulePreferences";
import logo from "assets/img/logo.png";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function Sidebar({ sidebarOpen, setSidebarOpen }) {
  const { sidebarCollapsed } = useGeneralStore();
  const companyPreferences = useGlobalStore((state) => state.companyPreferences);
  const { agttp, hasAccess } = useAuth();
  const location = useLocation();
  const isTrader = agttp === 1;

  const filterByAccess = (items) => {
    return items
      .filter((item) => !(item.traderOnly && !isTrader))
      .filter((item) => !(item.agentOnly && isTrader))
      .filter(
        (item) =>
          !item.permission ||
          isModuleEnabledForCompany(item.permission, companyPreferences)
      )
      .filter((item) => !item.permission || hasAccess(item.permission, 10))
      .map((item) => {
        if (item.children) {
          return { ...item, children: filterByAccess(item.children) };
        }
        return item;
      })
      .filter((item) => !item.children || item.children.length > 0);
  };

  const filteredNavigation = filterByAccess(navigation);

  // Function to check if a navigation item is current
  const isCurrentItem = (item) => {
    if (item.href) {
      return location.pathname === item.href;
    }
    if (item.children) {
      return item.children.some(child => {
        if (child.href) {
          return location.pathname === child.href;
        }
        if (child.children) {
          return child.children.some(grandChild => location.pathname === grandChild.href);
        }
        return false;
      });
    }
    return false;
  };

  // Function to check if a child item is current
  const isCurrentChild = (child) => {
    if (child.href) {
      return location.pathname === child.href;
    }
    if (child.children) {
      return child.children.some(grandChild => location.pathname === grandChild.href);
    }
    return false;
  };

  // Function to check if a grandchild item is current
  const isCurrentGrandChild = (grandChild) => {
    return location.pathname === grandChild.href;
  };

  const renderNavigationItem = (item, isMobile = false) => {
    const itemCurrent = isCurrentItem(item);

    if (!item.children) {
      return (
        <a
          href={item.href}
          className={classNames(
            itemCurrent
              ? "bg-gray-800 text-white"
              : "text-gray-400 hover:text-white hover:bg-gray-800",
            "group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold",
            !isMobile && sidebarCollapsed ? "justify-center" : ""
          )}
          title={!isMobile && sidebarCollapsed ? item.name : ""}
        >
          <item.icon className="h-6 w-6 shrink-0" aria-hidden="true" />
          {(isMobile || !sidebarCollapsed) && item.name}
        </a>
      );
    }

    if (isMobile) {
      return (
        <Disclosure as="div" className="space-y-1" defaultOpen={itemCurrent}>
          {({ open }) => (
            <>
              <Disclosure.Button
                className={classNames(
                  itemCurrent
                    ? "bg-gray-800 text-white"
                    : "text-gray-400 hover:text-white hover:bg-gray-800",
                  "flex items-center w-full text-left rounded-md p-2 gap-x-3 text-sm leading-6 font-semibold"
                )}
              >
                <item.icon className="h-6 w-6 shrink-0" aria-hidden="true" />
                {item.name}
                <ChevronRightIcon
                  className={classNames(
                    open ? "rotate-90 text-gray-500" : "text-gray-400",
                    "ml-auto h-5 w-5 shrink-0"
                  )}
                  aria-hidden="true"
                />
              </Disclosure.Button>
              <Disclosure.Panel className="mt-1 px-2">
                {item.children.map((subItem) => {
                  if (!subItem.children) {
                    return (
                      <a
                        key={subItem.name}
                        href={subItem.href}
                        className={classNames(
                          isCurrentChild(subItem)
                            ? "bg-gray-700 text-white"
                            : "text-gray-400 hover:text-white hover:bg-gray-800",
                          "block rounded-md py-2 pl-9 pr-2 text-sm leading-6"
                        )}
                      >
                        {subItem.name}
                      </a>
                    );
                  }

                  // Handle nested subcategories (like Reports > Inventory Reports > Order History)
                  return (
                    <Disclosure key={subItem.name} as="div" className="space-y-1" defaultOpen={isCurrentChild(subItem)}>
                      {({ open }) => (
                        <>
                          <Disclosure.Button
                            className={classNames(
                              isCurrentChild(subItem)
                                ? "bg-gray-700 text-white"
                                : "text-gray-400 hover:text-white hover:bg-gray-800",
                              "flex items-center w-full text-left rounded-md py-2 pl-9 pr-2 text-sm leading-6"
                            )}
                          >
                            {subItem.name}
                            <ChevronRightIcon
                              className={classNames(
                                open ? "rotate-90 text-gray-500" : "text-gray-400",
                                "ml-auto h-4 w-4 shrink-0"
                              )}
                              aria-hidden="true"
                            />
                          </Disclosure.Button>
                          <Disclosure.Panel className="mt-1">
                            {subItem.children.map((grandChild) => (
                              <a
                                key={grandChild.name}
                                href={grandChild.href}
                                className={classNames(
                                  isCurrentGrandChild(grandChild)
                                    ? "bg-gray-600 text-white"
                                    : "text-gray-400 hover:text-white hover:bg-gray-800",
                                  "block rounded-md py-2 pl-16 pr-2 text-sm leading-6"
                                )}
                              >
                                {grandChild.name}
                              </a>
                            ))}
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  );
                })}
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      );
    }

    // Desktop with potential collapse
    return (
      <div className={sidebarCollapsed ? "relative group" : ""}>
        <Disclosure as="div" className="space-y-1" defaultOpen={itemCurrent}>
          {({ open }) => (
            <>
              <Disclosure.Button
                className={classNames(
                  itemCurrent
                    ? "bg-gray-800 text-white"
                    : "text-gray-400 hover:text-white hover:bg-gray-800",
                  "flex items-center w-full text-left rounded-md p-2 gap-x-3 text-sm leading-6 font-semibold",
                  sidebarCollapsed ? "justify-center" : ""
                )}
                title={sidebarCollapsed ? item.name : ""}
              >
                <item.icon className="h-6 w-6 shrink-0" aria-hidden="true" />
                {!sidebarCollapsed && (
                  <>
                    {item.name}
                    <ChevronRightIcon
                      className={classNames(
                        open ? "rotate-90 text-gray-500" : "text-gray-400",
                        "ml-auto h-5 w-5 shrink-0"
                      )}
                      aria-hidden="true"
                    />
                  </>
                )}
              </Disclosure.Button>
              {!sidebarCollapsed && (
                <Disclosure.Panel className="mt-1 px-2">
                  {item.children.map((subItem) => {
                    if (!subItem.children) {
                      return (
                        <a
                          key={subItem.name}
                          href={subItem.href}
                          className={classNames(
                            isCurrentChild(subItem)
                              ? "bg-gray-700 text-white"
                              : "text-gray-400 hover:text-white hover:bg-gray-800",
                            "block rounded-md py-2 pl-9 pr-2 text-sm leading-6"
                          )}
                        >
                          {subItem.name}
                        </a>
                      );
                    }

                    // Handle nested subcategories (like Reports > Inventory Reports > Order History)
                    return (
                      <Disclosure key={subItem.name} as="div" className="space-y-1" defaultOpen={isCurrentChild(subItem)}>
                        {({ open }) => (
                          <>
                            <Disclosure.Button
                              className={classNames(
                                isCurrentChild(subItem)
                                  ? "bg-gray-700 text-white"
                                  : "text-gray-400 hover:text-white hover:bg-gray-800",
                                "flex items-center w-full text-left rounded-md py-2 pl-9 pr-2 text-sm leading-6"
                              )}
                            >
                              {subItem.name}
                              <ChevronRightIcon
                                className={classNames(
                                  open ? "rotate-90 text-gray-500" : "text-gray-400",
                                  "ml-auto h-4 w-4 shrink-0"
                                )}
                                aria-hidden="true"
                              />
                            </Disclosure.Button>
                            <Disclosure.Panel className="mt-1">
                              {subItem.children.map((grandChild) => (
                                <a
                                  key={grandChild.name}
                                  href={grandChild.href}
                                  className={classNames(
                                    isCurrentGrandChild(grandChild)
                                      ? "bg-gray-600 text-white"
                                      : "text-gray-400 hover:text-white hover:bg-gray-800",
                                    "block rounded-md py-2 pl-16 pr-2 text-sm leading-6"
                                  )}
                                >
                                  {grandChild.name}
                                </a>
                              ))}
                            </Disclosure.Panel>
                          </>
                        )}
                      </Disclosure>
                    );
                  })}
                </Disclosure.Panel>
              )}
            </>
          )}
        </Disclosure>

        {/* Tooltip for collapsed state */}
        {sidebarCollapsed && (
          <div className="absolute left-full top-0 ml-2 hidden group-hover:block z-50">
            <div className="bg-gray-800 text-white text-sm rounded-md py-2 px-3 shadow-lg border border-gray-700">
              <div className="font-medium mb-2">{item.name}</div>
              {item.children.map((subItem) => (
                <a
                  key={subItem.name}
                  href={subItem.href}
                  className={classNames(
                    isCurrentChild(subItem)
                      ? "text-white font-medium"
                      : "hover:text-gray-300",
                    "block py-1"
                  )}
                >
                  {subItem.name}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Mobile sidebar */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50 lg:hidden"
          onClose={setSidebarOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/80" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button
                      type="button"
                      className="-m-2.5 p-2.5"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon
                        className="h-6 w-6 text-white"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </Transition.Child>

                <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gray-900 px-6 pb-4 ring-1 ring-white/10">
                  <div className="flex h-16 shrink-0 items-center">
                    <img
                      className="h-8 w-auto"
                      src={logo}
                      alt="Company Logo"
                    />
                  </div>
                  <nav className="flex flex-1 flex-col">
                    <ul className="flex flex-1 flex-col gap-y-7">
                      <li>
                        <ul className="-mx-2 space-y-1">
                          {filteredNavigation.map((item) => (
                            <li key={item.name}>
                              {renderNavigationItem(item, true)}
                            </li>
                          ))}
                        </ul>
                      </li>
                    </ul>
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop sidebar */}
      <div
        className={`hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col transition-all duration-300 ${
          sidebarCollapsed ? "lg:w-16" : "lg:w-72"
        }`}
      >
        <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gray-900 px-3 pb-4">
          <div className="flex h-16 shrink-0 items-center justify-center">
            {!sidebarCollapsed && (
              <img
                className="h-8 w-auto"
                src={logo}
                alt="Company Logo"
              />
            )}
            {sidebarCollapsed && (
              <img
                className="h-8 w-8 object-contain"
                src={logo}
                alt="Company Logo"
              />
            )}
          </div>

          <nav className="flex flex-1 flex-col">
            <ul className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul className="-mx-2 space-y-1">
                  {filteredNavigation.map((item) => (
                    <li key={item.name}>{renderNavigationItem(item, false)}</li>
                  ))}
                </ul>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </>
  );
}
